import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, ExternalLink, ChevronLeft, Clock } from "lucide-react";
import { api } from "../lib/api";
import { formatDate, getDateParts } from "../lib/dateUtils";
import { trackEvent, trackEventBeacon } from "../lib/analytics";

export default function EventDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const data = await api.getEventBySlug(slug);
        setEvent(data);
        if (data?.id) {
          trackEvent("event_view", data.id, { title: data.title, slug: data.slug });
        }
      } catch {
        setError("Event not found");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug]);

  const today = new Date().toISOString().split('T')[0];
  const isUpcoming = event?.date >= today;

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse text-demolition-red font-russo text-2xl">LOADING...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-demolition-black flex flex-col items-center justify-center px-6">
        <h1 className="font-russo text-4xl text-white mb-4">EVENT NOT FOUND</h1>
        <p className="font-mono text-white/50 mb-8">The event you're looking for doesn't exist.</p>
        <Link
          to="/events"
          className="inline-flex items-center gap-2 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-widest hover:bg-demolition-red-hover transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Events
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-demolition-black min-h-screen" data-testid="event-detail-page">
      {/* Back Link */}
      <div className="px-6 md:px-12 py-6 bg-demolition-paper border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-white/60 hover:text-demolition-red transition-colors font-mono text-sm"
            data-testid="back-to-events"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Events
          </Link>
        </div>
      </div>

      {/* Event Header */}
      <section className="relative py-16 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 steel-mesh opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-4 mb-6">
            {isUpcoming ? (
              <span className="px-4 py-2 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider">
                Upcoming Event
              </span>
            ) : (
              <span className="px-4 py-2 bg-white/10 text-white/50 font-teko text-lg uppercase tracking-wider">
                Past Event
              </span>
            )}
          </div>
          
          <h1 
            className="font-russo text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tighter mb-6"
            data-testid="event-detail-title"
          >
            {event.title}
          </h1>

          <div className="flex flex-wrap gap-6 text-white/70">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-demolition-red" />
              <span className="font-mono">{formatDate(event.date)}</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-demolition-red" />
              <span className="font-mono">{event.location}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Event Content */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div className="border-l-4 border-demolition-red pl-6">
                <h2 className="font-russo text-2xl text-white uppercase tracking-tighter mb-4">
                  EVENT DETAILS
                </h2>
                <p className="font-mono text-white/70 leading-relaxed">
                  {event.description || "Join us for this exciting Demolition event! More details coming soon."}
                </p>
              </div>

              {/* What to Expect */}
              <div className="bg-demolition-subtle border border-white/10 p-8">
                <h3 className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mb-6">
                  WHAT TO EXPECT
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-demolition-red mt-2 flex-shrink-0" />
                    <p className="font-mono text-white/70">Meet and greet opportunities with wrestling legends</p>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-demolition-red mt-2 flex-shrink-0" />
                    <p className="font-mono text-white/70">Photo ops and autograph sessions</p>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-demolition-red mt-2 flex-shrink-0" />
                    <p className="font-mono text-white/70">Exclusive merchandise and memorabilia</p>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-demolition-red mt-2 flex-shrink-0" />
                    <p className="font-mono text-white/70">Live entertainment and presentations</p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Event Info Card */}
              <div className="bg-demolition-subtle border border-white/10 overflow-hidden">
                <div className="bg-demolition-red p-6 text-center">
                  <span className="font-russo text-5xl text-white block">
                    {getDateParts(event.date).day}
                  </span>
                  <span className="font-teko text-2xl text-white/90 uppercase">
                    {getDateParts(event.date).monthLong} {getDateParts(event.date).year}
                  </span>
                </div>
                
                <div className="p-6 space-y-4">
                  <div className="flex items-center gap-3 text-white/70">
                    <Clock className="w-5 h-5 text-demolition-red" />
                    <span className="font-mono text-sm">Time TBA</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/70">
                    <MapPin className="w-5 h-5 text-demolition-red" />
                    <span className="font-mono text-sm">{event.location}</span>
                  </div>
                </div>

                {event.external_url && (
                  <div className="p-6 border-t border-white/10">
                    <a
                      href={event.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEventBeacon("event_external_click", event.id, { title: event.title, url: event.external_url })}
                      className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-demolition-red text-white font-teko text-xl uppercase tracking-widest hover:bg-demolition-red-hover transition-colors"
                      data-testid="event-external-link"
                    >
                      <span>GET TICKETS</span>
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  </div>
                )}
              </div>

              {/* Share */}
              <div className="bg-demolition-subtle border border-white/10 p-6">
                <h3 className="font-teko text-lg text-demolition-chrome uppercase tracking-widest mb-4">
                  SHARE THIS EVENT
                </h3>
                <p className="font-mono text-white/50 text-sm">
                  Spread the word and bring your friends!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
